import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Caution, Dialog, SECONDARY_BUTTON } from '@/features/inventory/registerUi';
import type { AdminTenant } from './useTenants';
import { useSetRaadSecure } from './useRaadSecure';

/**
 * SLICE 14d — SWITCH RAAD SECURE ON OR OFF FOR ONE TENANT.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 TURNING IT ON IS A CUSTOMER-FACING ACT. THE DIALOG SAYS WHAT WILL APPEAR.
 * ─────────────────────────────────────────────────────────────────────────────
 * ON makes a "Block starting" control appear on the customer's own vehicles in
 * their own app, plus a Command PIN panel in their Settings. So the confirm
 * names the tenant and states, in words, exactly what shows up on their screen —
 * and states the thing that is easy to over-conclude: turning it on does NOT by
 * itself make any vehicle blockable. Each device still needs a clearance and the
 * user still needs a PIN; the dispatch gate is per-device and independent.
 *
 * OFF only removes controls, so it needs no ceremony beyond a plain confirm — but
 * it DOES need to say that any PIN a user already set is KEPT, so switching back
 * on does not force re-enrolment.
 *
 * 🔴 NO FREE-TEXT ACKNOWLEDGEMENT FIELD. The audit row already records who, when
 * and from→to; there is no external fact for prose to capture, and the
 * instruction was less ceremony, not more.
 *
 * `transitioned` off the response is read, never assumed: it distinguishes "you
 * just granted this" from "it was already on", which the boolean sent cannot.
 */
export function RaadSecureDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const setSecure = useSetRaadSecure();
  const [error, setError] = useState<string | null>(null);
  // null until resolved; then holds { enabled, transitioned } read off the wire.
  const [result, setResult] = useState<{ enabled: boolean; transitioned: boolean } | null>(null);

  // The dialog flips the CURRENT state — open on an off tenant to turn it on.
  const turningOn = !tenant.raadSecureEnabled;

  async function submit() {
    setError(null);
    try {
      const r = await setSecure.mutateAsync({ tenantId: tenant.id, enabled: turningOn });
      setResult({ enabled: r.raadSecureEnabled, transitioned: r.transitioned });
    } catch {
      setError(t('raadSecure.error'));
    }
  }

  const doneCopy = (() => {
    if (result === null) return '';
    if (result.enabled) {
      return result.transitioned
        ? t('raadSecure.done.granted', { tenant: tenant.name })
        : t('raadSecure.done.alreadyOn', { tenant: tenant.name });
    }
    return result.transitioned
      ? t('raadSecure.done.removed', { tenant: tenant.name })
      : t('raadSecure.done.alreadyOff', { tenant: tenant.name });
  })();

  return (
    <Dialog
      title={
        turningOn
          ? t('raadSecure.title.on', { tenant: tenant.name })
          : t('raadSecure.title.off', { tenant: tenant.name })
      }
      onClose={onClose}
      wide={turningOn}
    >
      {result !== null ? (
        <div className="grid gap-3">
          <p
            className={[
              'rounded-lg border p-3 text-[12.5px] text-raad-text',
              result.enabled
                ? 'border-raad-primary/40 bg-raad-primary/10'
                : 'border-raad-border bg-raad-bg/40',
            ].join(' ')}
          >
            {doneCopy}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary"
            >
              {t('raadSecure.doneButton')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {turningOn ? (
            <>
              <p className="text-[13px]">{t('raadSecure.on.intro', { tenant: tenant.name })}</p>
              <Caution>{t('raadSecure.on.notBlockable')}</Caution>
            </>
          ) : (
            <>
              <p className="text-[13px]">{t('raadSecure.off.intro', { tenant: tenant.name })}</p>
              <p className="rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
                {t('raadSecure.off.pinKept')}
              </p>
            </>
          )}

          {error ? (
            <p
              role="alert"
              className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
            >
              {error}
            </p>
          ) : null}

          <div className="mt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={onClose}
              disabled={setSecure.isPending}
            >
              {t('raadSecure.back')}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={setSecure.isPending}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
            >
              {setSecure.isPending
                ? t('raadSecure.working')
                : turningOn
                  ? t('raadSecure.on.confirm')
                  : t('raadSecure.off.confirm')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
